// src/design/format.ts
// Human-scale currency formatting. Use everywhere on user-facing surfaces.
// The technical form ("UGX 380.0M") is permitted only inside the audit trail / debug surfaces.

import { format as formatDate } from "date-fns";

export type CurrencyCode = "UGX" | "EUR" | "USD" | "GBP";

const SYMBOLS: Record<CurrencyCode, string> = {
  UGX: "",
  EUR: "€",
  USD: "$",
  GBP: "£",
};

/**
 * Compact figure with no currency code, e.g. "48B", "2.64B", "420M", "5.2M", "750K".
 * Currencies are always shown as the three-letter code (see formatMoney) to keep
 * figures short and unambiguous.
 */
export function formatCompactCurrency(raw: number): string {
  if (raw >= 1_000_000_000) {
    const v = raw / 1_000_000_000;
    return `${v < 10 ? v.toFixed(2) : String(Math.round(v))}B`;
  }
  if (raw >= 1_000_000) {
    const v = raw / 1_000_000;
    return `${Number.isInteger(v) ? String(v) : v.toFixed(1)}M`;
  }
  if (raw >= 1_000) return `${Math.round(raw / 1_000)}K`;
  return raw.toLocaleString();
}

/** Full money string with the three-letter code, e.g. "1.24B UGX". */
export function formatMoney(raw: number, currency: CurrencyCode = "UGX"): string {
  return `${formatCompactCurrency(raw)} ${currency}`;
}

/** Back-compat alias. All money now renders compact + three-letter code. */
export const formatHumanCurrency = formatMoney;

// ---------------------------------------------------------------------------
// Multi-currency: a fixed mock FX table and a converter (spec §2.6).
// The canonical ledger base unit is UGX; figures convert for display only.
// ---------------------------------------------------------------------------

/** "1 unit of code = N UGX". Mirrors FX_TO_UGX in domain/types. */
const FX_TO_UGX: Record<CurrencyCode, number> = {
  UGX: 1,
  EUR: 4_050,
  GBP: 4_720,
  USD: 3_750,
};

/** Convert an amount between two currencies via the UGX base. */
export function convert(amount: number, from: CurrencyCode, to: CurrencyCode): number {
  if (from === to) return amount;
  const inUgx = amount * FX_TO_UGX[from];
  return inUgx / FX_TO_UGX[to];
}

/**
 * Format a UGX-base figure in the requested display currency, e.g.
 * formatMoneyIn(2_800_000_000, "EUR") -> "691M EUR". UGX passes through unchanged.
 */
export function formatMoneyIn(baseUgx: number, display: CurrencyCode): string {
  return formatMoney(convert(baseUgx, "UGX", display), display);
}

/** The trailing currency label for stat tiles (the three-letter code). */
export function currencyUnit(currency: CurrencyCode = "UGX"): string {
  return currency;
}

/** Technical currency form for audit / debug surfaces only, e.g. "UGX 380.0M". */
export function formatTechnicalCurrency(
  raw: number,
  currency: CurrencyCode = "UGX",
): string {
  const sym = SYMBOLS[currency];
  const prefix = sym ? sym : `${currency} `;
  if (raw >= 1_000_000_000) return `${prefix}${(raw / 1_000_000_000).toFixed(1)}B`;
  if (raw >= 1_000_000) return `${prefix}${(raw / 1_000_000).toFixed(1)}M`;
  if (raw >= 1_000) return `${prefix}${(raw / 1_000).toFixed(1)}K`;
  return `${prefix}${raw.toLocaleString()}`;
}

/** Percentage formatting from a 0..1 ratio. */
export function formatPct(ratio: number, digits = 0): string {
  return `${(ratio * 100).toFixed(digits)}%`;
}

// ---------------------------------------------------------------------------
// Non-lossy + non-currency formatters (brief §2.4 / §7).
// Compact forms (691K) lead; these give the exact figure on drill-down, terse
// feed times, and one canonical card date so the patterns never drift inline.
// ---------------------------------------------------------------------------

/** Whole-number with grouped thousands, e.g. "2,800,000" — for counts and tallies. */
export function formatCount(n: number): string {
  return Math.round(n).toLocaleString("en-US");
}

/**
 * The exact figure with grouped thousands and the three-letter code, e.g.
 * "2,800,000,000 UGX". Use on audit / tooltip drill-downs where the compact
 * form (2.80B) would round away detail.
 */
export function formatFull(raw: number, currency: CurrencyCode = "UGX"): string {
  return `${formatCount(raw)} ${currency}`;
}

type DateInput = Date | string | number;

/** Canonical card date, e.g. "15 Feb 2026". */
export function formatCardDate(input: DateInput): string {
  return formatDate(new Date(input), "d MMM yyyy");
}

/** Card date with time, e.g. "15 Feb 2026, 14:30". */
export function formatDateTime(input: DateInput): string {
  return formatDate(new Date(input), "d MMM yyyy, HH:mm");
}

/**
 * Terse relative time for feeds and queues, e.g. "now", "5m", "3h", "8d", "2mo", "1y".
 * Days run up to a month (the brief shows "8d", not weeks). `from` defaults to the
 * current time; pass a fixed reference in tests.
 */
export function formatRelativeShort(input: DateInput, from: DateInput = new Date()): string {
  const ms = new Date(from).getTime() - new Date(input).getTime();
  const sec = Math.max(0, Math.floor(ms / 1000));
  if (sec < 60) return "now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo`;
  return `${Math.floor(day / 365)}y`;
}
